import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * useWallet — global hook that exposes the tenant's credit wallet status.
 *
 * Polls /api/credits/wallet every 30s and re-fetches on demand. Returns:
 *   { wallet, refresh, loading }
 *
 * Wallet shape (mirrors backend `/api/credits/wallet`):
 *   {
 *     balance, monthly_grant, period_used, percent_used,
 *     threshold: "ok"|"warn80"|"warn90"|"critical",
 *     plan_code, enforcement_enabled, last_alert_threshold
 *   }
 *
 * Light-weight by design: no global store, no provider needed. Each
 * component that needs the wallet calls the hook directly; the network
 * cost is one cheap GET every 30s per mounted instance, and React
 * deduplicates concurrent fetches via the request cache below.
 */

let _cache = null;        // last successful wallet
let _cacheAt = 0;
let _inflight = null;     // in-flight promise (prevents N parallel hits)

async function fetchWallet(force = false) {
  const now = Date.now();
  if (!force && _cache && now - _cacheAt < 10_000) {
    return _cache;
  }
  if (_inflight) return _inflight;
  _inflight = api.get("/credits/wallet")
    .then(({ data }) => {
      _cache = data;
      _cacheAt = Date.now();
      return data;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function useWallet({ pollMs = 30_000 } = {}) {
  const [wallet, setWallet] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  const refresh = useCallback(async (force = true) => {
    try {
      setLoading(!_cache);
      const w = await fetchWallet(force);
      setWallet(w);
    } catch {
      // Silently ignore — wallet probe failure should never break the UI.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh(false);
    const t = setInterval(() => refresh(true), pollMs);

    // Refetch immediately when a 402 fires so the banner / modal reflect
    // the latest balance without waiting for the next poll tick.
    const onExhaust = () => refresh(true);
    window.addEventListener("agentos:credits_exhausted", onExhaust);
    return () => {
      clearInterval(t);
      window.removeEventListener("agentos:credits_exhausted", onExhaust);
    };
  }, [refresh, pollMs]);

  return { wallet, refresh, loading };
}

/* helper to invalidate the cache from anywhere (used after a pack purchase) */
export function invalidateWallet() {
  _cache = null;
  _cacheAt = 0;
}
